import { mapFeatures, mapMediaItem } from '@vp/digital-common-data-model-lib';
import React from 'react';
import { useLocale } from '@vp/digital-locale-lib';
import { Helmet } from 'react-helmet';

var SEOSiteName;
(function (SEOSiteName) {
    SEOSiteName["OpenGraph"] = "Vistaprint";
    SEOSiteName["TwitterCard"] = "@Vistaprint";
})(SEOSiteName || (SEOSiteName = {}));
var SEOContentViewType;
(function (SEOContentViewType) {
    SEOContentViewType["OpenGraph"] = "websites";
    SEOContentViewType["TwitterCard"] = "summary_large_image";
})(SEOContentViewType || (SEOContentViewType = {}));
var SEOMediaKeys;
(function (SEOMediaKeys) {
    SEOMediaKeys["Facebook"] = "seoFacebook";
    SEOMediaKeys["Twitter"] = "seoTwitter";
    SEOMediaKeys["Google"] = "seoGoogle";
})(SEOMediaKeys || (SEOMediaKeys = {}));
var OpenGraphProps;
(function (OpenGraphProps) {
    OpenGraphProps["siteName"] = "og:site_name";
    OpenGraphProps["contentViewType"] = "og:type";
    OpenGraphProps["title"] = "og:title";
    OpenGraphProps["description"] = "og:description";
    OpenGraphProps["imageUrl"] = "og:image";
    OpenGraphProps["imageAlt"] = "og:image:alt";
    OpenGraphProps["url"] = "og:url";
    OpenGraphProps["locale"] = "og:locale";
})(OpenGraphProps || (OpenGraphProps = {}));
var TwitterCardProps;
(function (TwitterCardProps) {
    TwitterCardProps["siteName"] = "twitter:site";
    TwitterCardProps["contentViewType"] = "twitter:card";
    TwitterCardProps["title"] = "twitter:title";
    TwitterCardProps["description"] = "twitter:description";
    TwitterCardProps["imageUrl"] = "twitter:image";
    TwitterCardProps["imageAlt"] = "twitter:image:alt";
})(TwitterCardProps || (TwitterCardProps = {}));
var MicrodataProps;
(function (MicrodataProps) {
    MicrodataProps["title"] = "name";
    MicrodataProps["description"] = "description";
    MicrodataProps["imageUrl"] = "image";
    MicrodataProps["url"] = "url";
})(MicrodataProps || (MicrodataProps = {}));

var resourceType = {
    image: 'image',
    video: 'video',
};
var mapMediaType = function (media, key, resourceType) {
    var mediaItem = media.find(function (mediaLink) {
        var _a;
        return mediaLink &&
            ((_a = mapMediaItem(mediaLink).media) === null || _a === void 0 ? void 0 : _a.resourceType) === resourceType &&
            mapMediaItem(mediaLink).key === key;
    });
    return mediaItem && mapMediaItem(mediaItem);
};
var mapSEO = function (seoContentful) {
    var seo = {
        title: seoContentful.metaTitle,
    };
    if (seoContentful.metaDescription) {
        seo.description = seoContentful.metaDescription.metaDescription;
    }
    if (seoContentful.metaRobot) {
        seo.robots = seoContentful.metaRobot;
    }
    if (seoContentful.faqs) {
        seo.faqs = {
            title: seoContentful.faqs.title,
            features: mapFeatures(seoContentful.faqs.features),
        };
    }
    if (seoContentful.media) {
        seo.facebookImage = mapMediaType(seoContentful.media, SEOMediaKeys.Facebook, resourceType.image);
        seo.facebookVideo = mapMediaType(seoContentful.media, SEOMediaKeys.Facebook, resourceType.video);
        seo.googleImage = mapMediaType(seoContentful.media, SEOMediaKeys.Google, resourceType.image);
        seo.googleVideo = mapMediaType(seoContentful.media, SEOMediaKeys.Google, resourceType.video);
        seo.twitterImage = mapMediaType(seoContentful.media, SEOMediaKeys.Twitter, resourceType.image);
        seo.twitterVideo = mapMediaType(seoContentful.media, SEOMediaKeys.Twitter, resourceType.video);
    }
    return seo;
};

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var removeFragment = function (url) {
    var fragmentIndex = url.indexOf('#');
    var queryParamsIndex = url.indexOf('?');
    if (fragmentIndex !== -1) {
        url = url.substring(0, fragmentIndex);
    }
    if (queryParamsIndex !== -1) {
        url = url.substring(0, queryParamsIndex);
    }
    return url;
};
var CanonicalComponent = function (_a) {
    var url = _a.url;
    var canonicalUrl = removeFragment(url);
    return (React.createElement(React.Fragment, null,
        React.createElement(Helmet, null, url && React.createElement("link", { rel: "canonical", href: canonicalUrl }))));
};

var MicrodataComponent = function (_a) {
    var content = _a.content;
    var title = content.title, description = content.description, imageMedia = content.googleImage, url = content.url;
    return (React.createElement(React.Fragment, null,
        React.createElement(Helmet, null,
            title && React.createElement("meta", { itemProp: MicrodataProps.title, content: title }),
            description && (React.createElement("meta", { itemProp: MicrodataProps.description, content: description })),
            imageMedia && (React.createElement("meta", { itemProp: MicrodataProps.imageUrl, content: imageMedia.media.secureUrl })),
            url && React.createElement("meta", { itemProp: MicrodataProps.url, content: url }))));
};

var OpenGraphComponent = function (_a) {
    var content = _a.content;
    var siteName = content.siteName, contentViewType = content.contentViewType, title = content.title, description = content.description, imageMedia = content.facebookImage, url = content.url, locale = content.locale;
    return (React.createElement(React.Fragment, null,
        React.createElement(Helmet, null,
            siteName && (React.createElement("meta", { property: OpenGraphProps.siteName, content: siteName })),
            contentViewType && (React.createElement("meta", { property: OpenGraphProps.contentViewType, content: contentViewType })),
            title && React.createElement("meta", { property: OpenGraphProps.title, content: title }),
            description && (React.createElement("meta", { property: OpenGraphProps.description, content: description })),
            imageMedia && (React.createElement("meta", { property: OpenGraphProps.imageUrl, content: imageMedia.media.secureUrl })),
            imageMedia && (React.createElement("meta", { property: OpenGraphProps.imageAlt, content: imageMedia.altText })),
            url && React.createElement("meta", { property: OpenGraphProps.url, content: url }),
            locale && React.createElement("meta", { property: OpenGraphProps.locale, content: locale }))));
};

var RobotsComponent = function (_a) {
    var robots = _a.robots;
    return (React.createElement(React.Fragment, null,
        React.createElement(Helmet, null, robots && React.createElement("meta", { name: "robots", content: robots }))));
};

var SEOBaseComponent = function (_a) {
    var title = _a.title, description = _a.description;
    return (React.createElement(React.Fragment, null,
        React.createElement(Helmet, null,
            React.createElement("title", null, title),
            description && React.createElement("meta", { name: "description", content: description }))));
};

var TwitterCardComponent = function (_a) {
    var content = _a.content;
    var siteName = content.siteName, contentViewType = content.contentViewType, title = content.title, description = content.description, imageMedia = content.twitterImage;
    return (React.createElement(React.Fragment, null,
        React.createElement(Helmet, null,
            siteName && (React.createElement("meta", { name: TwitterCardProps.siteName, content: siteName })),
            contentViewType && (React.createElement("meta", { name: TwitterCardProps.contentViewType, content: contentViewType })),
            title && React.createElement("meta", { name: TwitterCardProps.title, content: title }),
            description && (React.createElement("meta", { name: TwitterCardProps.description, content: description })),
            imageMedia && (React.createElement("meta", { name: TwitterCardProps.imageUrl, content: imageMedia.media.secureUrl })),
            imageMedia && (React.createElement("meta", { name: TwitterCardProps.imageAlt, content: imageMedia.altText })))));
};

var FAQType;
(function (FAQType) {
    FAQType["Question"] = "Question";
    FAQType["Answer"] = "Answer";
})(FAQType || (FAQType = {}));
var MarkupType;
(function (MarkupType) {
    MarkupType["BreadcrumbList"] = "BreadcrumbList";
    MarkupType["Product"] = "Product";
    MarkupType["FAQ"] = "FAQPage";
})(MarkupType || (MarkupType = {}));
var BrandType;
(function (BrandType) {
    BrandType["Thing"] = "Thing";
})(BrandType || (BrandType = {}));
var SellerType;
(function (SellerType) {
    SellerType["Organization"] = "Organization";
})(SellerType || (SellerType = {}));
var OffersType;
(function (OffersType) {
    OffersType["AggregatedOffer"] = "AggregatedOffer";
})(OffersType || (OffersType = {}));
var RatingType;
(function (RatingType) {
    RatingType["AggregatedRating"] = "AggregatedRating";
})(RatingType || (RatingType = {}));
var Organization;
(function (Organization) {
    Organization["Name"] = "Vistaprint";
})(Organization || (Organization = {}));
var SchemaUrl;
(function (SchemaUrl) {
    SchemaUrl["Base"] = "https://schema.org";
    SchemaUrl["InStock"] = "https://schema.org/InStock";
})(SchemaUrl || (SchemaUrl = {}));

var createProductLDJson = function (productData) {
    var _a;
    var ldJson = {
        '@context': SchemaUrl.Base,
        '@type': MarkupType.Product,
        '@id': productData.productKey,
        name: productData.productName,
        sku: productData.productKey,
        mpn: productData.productKey,
        description: productData.description,
        brand: {
            '@type': BrandType.Thing,
            name: Organization.Name,
        },
        offers: {
            '@type': OffersType.AggregatedOffer,
            url: productData.url,
            availability: SchemaUrl.InStock,
            seller: {
                '@type': SellerType.Organization,
                name: Organization.Name,
            },
            lowPrice: productData.offerPrice,
            priceCurrency: productData.offerPriceCurrency,
            offerCount: productData.offerCount,
        },
        image: (_a = productData.image) === null || _a === void 0 ? void 0 : _a.media.secureUrl,
    };
    if (productData.productRatingAvailablity) {
        var ldJsonWithRating = __assign(__assign({}, ldJson), { aggregatedRating: {
                '@type': RatingType.AggregatedRating,
                ratingValue: productData.productRating,
                reviewCount: productData.productReviewCount,
            } });
        return ldJsonWithRating;
    }
    return ldJson;
};
var ProductMarkupComponent = function (_a) {
    var content = _a.content;
    var productLDJson = createProductLDJson(content);
    return (React.createElement(React.Fragment, null,
        React.createElement(Helmet, null,
            React.createElement("script", { type: "application/ld+json" }, JSON.stringify(productLDJson)))));
};

var createFaqLDJson = function (faqData) {
    var ldJson = {
        '@context': SchemaUrl.Base,
        '@type': MarkupType.FAQ,
        mainEntity: faqData === null || faqData === void 0 ? void 0 : faqData.features.map(function (feature) { return ({
            '@type': FAQType.Question,
            name: feature.title,
            acceptedAnswer: {
                '@type': FAQType.Answer,
                text: feature.description,
            },
        }); }),
    };
    return ldJson;
};
var FaqMarkupComponent = function (_a) {
    var content = _a.content;
    var faqLDJson = createFaqLDJson(content);
    return (React.createElement(React.Fragment, null,
        React.createElement(Helmet, null,
            React.createElement("script", { type: "application/ld+json" }, JSON.stringify(faqLDJson)))));
};

var SEOComponent = function (_a) {
    var content = _a.content, productMarkup = _a.productMarkup, faqMarkup = _a.faqMarkup, markupData = _a.markupData;
    var title = content.title, description = content.description, robots = content.robots, faqs = content.faqs;
    var locale = useLocale().identifier;
    var isBrowser = function () { return typeof window !== 'undefined'; };
    var url = isBrowser() ? window.location.href : '';
    var seoContent = __assign({ url: url, locale: locale }, content);
    var openGraphContent = __assign({ siteName: SEOSiteName.OpenGraph, contentViewType: SEOContentViewType.OpenGraph }, seoContent);
    var twitterCardContent = __assign({ siteName: SEOSiteName.TwitterCard, contentViewType: SEOContentViewType.TwitterCard }, seoContent);
    var productMarkupContent = __assign({ url: url, title: title, description: description, image: content.googleImage, faqs: faqs }, markupData);
    return (React.createElement(React.Fragment, null,
        React.createElement(SEOBaseComponent, { title: title, description: description }),
        React.createElement(RobotsComponent, { robots: robots }),
        React.createElement(CanonicalComponent, { url: url }),
        React.createElement(MicrodataComponent, { content: seoContent }),
        React.createElement(OpenGraphComponent, { content: openGraphContent }),
        React.createElement(TwitterCardComponent, { content: twitterCardContent }),
        productMarkup && (React.createElement(ProductMarkupComponent, { content: productMarkupContent })),
        faqMarkup && React.createElement(FaqMarkupComponent, { content: faqs })));
};

var SEOContainer = function (_a) {
    var content = _a.content, productMarkup = _a.productMarkup, faqMarkup = _a.faqMarkup, markupData = _a.markupData;
    return (React.createElement(React.Fragment, null,
        React.createElement(SEOComponent, { content: content, productMarkup: productMarkup, faqMarkup: faqMarkup, markupData: markupData })));
};

export { BrandType, FAQType, MarkupType, MicrodataProps, OffersType, OpenGraphProps, Organization, RatingType, SEOContainer, SEOContentViewType, SEOMediaKeys, SEOSiteName, SchemaUrl, SellerType, TwitterCardProps, mapSEO };
